import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, CheckCircle, ChatCircleText, Code, FilePdf, Gauge, Globe, Lightning, Lock, MagnifyingGlass, ShieldCheck, Sparkle } from "@phosphor-icons/react";
import { useAuth } from "@/lib/auth-context";
import PublicHeader from "@/components/PublicHeader";
import Footer from "@/components/Footer";
import DarkScannerHero from "@/components/DarkScannerHero";
import PDFStack from "@/components/PDFStack";
import ConnectingDots from "@/components/ConnectingDots";

export default function LandingPage() {
  const { user } = useAuth();
  const nav = useNavigate();

  const FEATURES = [
    { icon: MagnifyingGlass, title: "SEO Coverage", desc: "Title, meta, headings, canonicals, social cards, robots, sitemap." },
    { icon: Sparkle, title: "GEO — AI Search", desc: "llms.txt, JSON-LD entity schema, author E-E-A-T signals, AI-crawler access — how ChatGPT, Perplexity & Gemini cite you." },
    { icon: ChatCircleText, title: "AEO — Featured Snippets", desc: "FAQ / HowTo / Speakable schema, question-led headings, snippet-sized answers, TL;DR blocks." },
    { icon: Lightning, title: "Performance", desc: "Page weight, response time, compression, redirects, image loading." },
    { icon: ShieldCheck, title: "Security", desc: "HTTPS, HSTS, CSP, X-Frame, mixed content, server-version leaks." },
    { icon: CheckCircle, title: "Accessibility", desc: "Alt text, labels, landmarks, skip links, language attribute." },
    { icon: Code, title: "Tech Stack", desc: "BuiltWith-style fingerprint: CMS, frameworks, analytics, ads, CDN, payments, chat, fonts, hosting — across 100+ patterns." },
    { icon: Gauge, title: "Smart Recommendations", desc: "Each finding paired with the exact fix and why it matters." },
  ];

  return (
    <div className="min-h-screen bg-background">
      <PublicHeader active="home" />

      <section className="relative overflow-hidden border-b border-border bg-[#050507] text-white">
        {/* Deep base */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse 70% 50% at 20% 0%, rgba(255,255,255,0.04), transparent 60%)," +
              "linear-gradient(180deg, #0A0A0C 0%, #050507 100%)",
          }}
        />
        {/* Animated connecting-dots network */}
        <ConnectingDots />
        {/* Vignette to fade the dots near edges & improve text legibility */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse 60% 50% at 30% 50%, rgba(5,5,7,0.55), transparent 70%)",
          }}
        />

        <div className="relative max-w-[1280px] mx-auto px-6 lg:px-10 py-20 lg:py-28 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <div className="label-overline mb-6 fade-in-up text-white/50">A Division of Yellow Thinking</div>
            <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95] mb-6 fade-in-up stagger-1">
              Find every flaw <br />
              in your website. <br />
              <span className="text-brand">Before users do.</span>
            </h1>
            <p className="text-lg text-white/70 max-w-xl leading-relaxed mb-8 fade-in-up stagger-2">
              Auditor runs a 60-point technical inspection of any URL — SEO,
              GEO &amp; AEO (so AI search engines actually cite you),
              performance, security, accessibility, and tech-stack
              fingerprinting — then hands you a prioritised, plain-language
              list of the things to fix. No jargon, no vanity scores. Just the
              work that matters.
            </p>
            <div className="flex flex-wrap items-center gap-3 fade-in-up stagger-3">
              <Link to={user ? "/app/new" : "/register"} data-testid="hero-cta-primary"
                className="group inline-flex items-center gap-2 bg-white text-ink px-6 py-3.5 rounded-sm font-semibold hover:bg-brand hover:text-white transition-all hover:-translate-y-0.5">
                Run my first audit
                <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform" />
              </Link>
              <Link to="/about" data-testid="hero-cta-secondary"
                className="inline-flex items-center gap-2 border border-white/30 px-6 py-3.5 rounded-sm font-semibold text-white hover:bg-white hover:text-ink transition-colors">
                See what we check
              </Link>
            </div>
            <div className="mt-10 flex items-center gap-6 text-sm text-white/60 fade-in-up stagger-4">
              <span className="flex items-center gap-1.5"><Lock size={14} /> Private audits</span>
              <span className="flex items-center gap-1.5"><Globe size={14} /> PDF + JSON export</span>
              <span className="flex items-center gap-1.5"><Lightning size={14} /> Sixty real checks</span>
            </div>
          </div>

          <div className="lg:col-span-5 fade-in-up stagger-3">
            <DarkScannerHero />
          </div>
        </div>

        <style>{`
          @keyframes drift-slow {
            0%   { transform: translate(0, 0); }
            50%  { transform: translate(-40px, 30px); }
            100% { transform: translate(0, 0); }
          }
          .drift-slow { animation: drift-slow 14s ease-in-out infinite; }
        `}</style>
      </section>

      {/* Sample report stack */}
      <section className="border-b border-border bg-background">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-20 lg:py-24 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 order-2 lg:order-1">
            <div className="relative h-[460px] lg:h-[520px]" data-testid="landing-pdf-stack">
              <PDFStack className="h-full" />
            </div>
          </div>
          <div className="lg:col-span-6 order-1 lg:order-2">
            <div className="label-overline mb-4">The deliverable</div>
            <h2 className="font-display text-4xl lg:text-5xl font-black tracking-tighter leading-tight mb-6">
              A printable report <br /> you can hand to a dev.
            </h2>
            <p className="text-lg text-ink-muted leading-relaxed mb-6 max-w-xl">
              Every audit comes back as a designed PDF — branded cover,
              category breakdowns with colour-coded score gauges, a prioritised
              fix-list, quick wins, and long-term improvements. Drop it into a
              client email or print it for a project kick-off.
            </p>
            <div className="grid grid-cols-2 gap-3 max-w-md mb-6">
              {[
                ["8", "pages per report"],
                ["60", "checks audited"],
                ["7", "category scores"],
                ["~30s", "average run"],
              ].map(([n, l]) => (
                <div key={l} className="surface-card p-4">
                  <div className="num-display text-3xl">{n}</div>
                  <div className="text-xs text-ink-muted">{l}</div>
                </div>
              ))}
            </div>
            <Link to="/about" data-testid="landing-see-sample-link"
              className="inline-flex items-center gap-1 text-sm font-semibold text-ink hover:text-brand">
              <FilePdf size={16} weight="duotone" /> See a sample report
              <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      <section className="border-b border-border bg-white">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-20">
          <div className="grid lg:grid-cols-12 gap-10 mb-12">
            <div className="lg:col-span-5">
              <div className="label-overline mb-4">What gets checked</div>
              <h2 className="font-display text-4xl lg:text-5xl font-black tracking-tight leading-tight">
                Sixty real checks. <br /> Zero fluff.
              </h2>
            </div>
            <p className="lg:col-span-7 text-ink-muted text-lg leading-relaxed pt-2">
              Every audit covers the same comprehensive surface that paid tools
              charge $99/month for — plus a plain-language remediation plan
              tailored to your stack.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-border border border-border">
            {FEATURES.map(({ icon: Icon, title, desc }, i) => (
              <div key={title} data-testid={`feature-${i}`} className="bg-white p-8 hover:bg-secondary transition-colors">
                <Icon size={28} className="text-brand mb-4" weight="duotone" />
                <div className="font-display font-bold text-xl mb-2">{title}</div>
                <p className="text-sm text-ink-muted leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
          <div className="mt-10 text-center">
            <Link to="/about" data-testid="features-see-all"
              className="inline-flex items-center gap-1 text-sm font-semibold text-ink hover:text-brand transition-colors">
              See the full inspection list <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      <section className="bg-ink text-white">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-20 grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <div className="label-overline text-white/60 mb-4">Ready when you are</div>
            <h2 className="font-display text-5xl lg:text-6xl font-black tracking-tighter leading-[0.95]">
              Audit your first <br /> URL in 30 seconds.
            </h2>
          </div>
          <div className="lg:col-span-4 flex lg:justify-end">
            <button
              onClick={() => nav(user ? "/app/new" : "/register")}
              data-testid="footer-cta-button"
              className="bg-white text-ink px-6 py-4 rounded-sm font-semibold hover:bg-brand hover:text-white transition-colors inline-flex items-center gap-2"
            >
              Get started — free
              <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
